import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import {
    createEmptyTransportationService,
    Service,
    ServicePricing,
    TransportationMetadata,
    MetadataDuration,
    StaffMember
} from 'model/Service';
import { AssociatedForm } from 'model/Forms';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { saveBulkServicesThunk } from 'actions/services/ServicesActions';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import { TransportationPetTypeStep } from './Steps/PetType/TransportationPetTypeStep';
import { TransportationDetailsStep } from './Steps/Details/TransportationDetailsStep';
import { TransportationPricingStep } from './Steps/Pricing/TransportationPricingStep';
import { TransportationStaffStep } from './Steps/Staff/TransportationStaffStep';
import styles from './TransportationForm.module.scss';

interface TransportationFormProps {
    mode: 'add' | 'edit';
    service?: Service;
    onClose: () => void;
}

interface TransportationDetailsFormData {
    name: string;
    description?: string;
    selectedVehicles: Array<number>;
    color?: string;
    forms?: AssociatedForm[];
}

export const TransportationForm: React.FC<TransportationFormProps> = ({ mode, service, onClose }) => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const fetchPendingRef = useRef(false);

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);
    const [showExitPrompt, setShowExitPrompt] = useState(false);

    const [transportationService, setTransportationService] = useState<Service>(() => {
        if (mode === 'edit' && service) {
            return service;
        }
        return createEmptyTransportationService(masterData, marketplace);
    });

    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsSaving(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsSaving(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces?: Service['marketplaces']) => {
        setTransportationService(prev => ({
            ...prev,
            petTypes,
            marketplaces: marketplaces ?? prev.marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleDetailsStep = (details: {
        name: string;
        description: string;
        selectedVehicles: Array<number>;
        color?: string;
        forms?: AssociatedForm[];
        marketplaces: Service['marketplaces'];
    }) => {
        const updatedMetadata = {
            vehicles_enabled: true,
            selected_vehicles: details.selectedVehicles
        };

        setTransportationService(prev => ({
            ...prev,
            name: details.name,
            description: details.description,
            color: details.color || '',
            forms: details.forms || [],
            marketplaces: details.marketplaces,
            transportationData: {
                vehiclesEnabled: true,
                selectedVehicles: details.selectedVehicles
            } as TransportationMetadata,
            duration: {
                metadata: JSON.stringify(updatedMetadata)
            } as MetadataDuration
        }));
        setFormStep(prev => prev + 1);
    };

    const handlePricingStep = (pricing: ServicePricing, marketplaces: Service['marketplaces']) => {
        setTransportationService(prev => ({
            ...prev,
            pricing,
            marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleStaffStep = async (staff: Array<StaffMember>, marketplaces: Service['marketplaces']) => {
        try {
            setIsSaving(true);
            const finalService: Service = {
                ...transportationService,
                staff,
                marketplaces
            };
            await dispatch(saveBulkServicesThunk(finalService));
        } catch (error) {
            setIsSaving(false);
        }
    };

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const getActiveStep = () => {
        if (mode === 'edit' && !isEditingSpecificStep) {
            return 4;
        }
        return formStep;
    };

    const transportationDetailsFormData: TransportationDetailsFormData = useMemo(
        () => ({
            name: transportationService.name,
            description: transportationService.description,
            selectedVehicles: transportationService.transportationData?.selectedVehicles || [],
            color: transportationService.color || undefined,
            forms: transportationService.forms || []
        }),
        [
            transportationService.name,
            transportationService.description,
            transportationService.transportationData?.selectedVehicles,
            transportationService.color,
            transportationService.forms
        ]
    );

    const title = mode === 'edit' ? `Editing ${transportationService.name}` : 'Adding a Transportation Service';
    const successTitle = mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    const successMessage = (
        <p className={styles.successDescription}>
            <b>{transportationService.name}</b> was {mode === 'edit' ? 'updated' : 'added'} successfully.
        </p>
    );

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={title}
                onClose={onClose}
            >
                <SuccessScreen
                    title={successTitle}
                    message={successMessage}
                    buttonLabel="Go to Transportation Services"
                    onClick={onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo={false}
            title={title}
            onClose={() => setShowExitPrompt(true)}
            headerClassName={styles.transportationFormHeader}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={onClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            {isLoadingOrganizationServices ? (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Pet Type and Locations',
                                description: 'Select pet categories for this service',
                                component: (
                                    <TransportationPetTypeStep
                                        selectedPetTypes={transportationService.petTypes}
                                        marketplaces={transportationService.marketplaces}
                                        serviceId={transportationService.id}
                                        onNext={handlePetTypeStep}
                                    />
                                )
                            },
                            {
                                title: 'Transportation Service Detail',
                                component: (
                                    <TransportationDetailsStep
                                        formData={transportationDetailsFormData}
                                        marketplaces={transportationService.marketplaces}
                                        serviceId={transportationService.id}
                                        onNext={handleDetailsStep}
                                    />
                                )
                            },
                            {
                                title: 'Pricing',
                                description: 'Set up pricing and taxes for this service',
                                component: (
                                    <TransportationPricingStep
                                        selectedPetTypes={transportationService.petTypes}
                                        marketplaces={transportationService.marketplaces}
                                        existingService={mode === 'edit' ? service : undefined}
                                        onNext={handlePricingStep}
                                    />
                                )
                            },
                            {
                                title: 'Staff',
                                component: (
                                    <TransportationStaffStep
                                        marketplaces={transportationService.marketplaces}
                                        organizationServicesInfo={organizationServicesInfo}
                                        onComplete={handleStaffStep}
                                        isLoading={isSaving}
                                    />
                                )
                            }
                        ]}
                        activeStep={getActiveStep()}
                        onEdit={editStepHandler}
                        flexibleNavigation={mode === 'edit'}
                    />
                </div>
            )}
        </OverFullWindow>
    );
};

export default TransportationForm;
